import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  ContentChild,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule } from '@angular/forms';
import { CwDropdown, CwDropdownGroup, CwDropdownItem } from '@bbraun/cortex-angular';
import { InputStatus } from '@bbraun/cortex-web';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, startWith, switchMap, tap } from 'rxjs';
import { CustomShadowCssDirective } from '~/app/directives/CustomShadowCss';
import { DropdownMenuToggleDirective } from '~/app/directives/DropdownMenuToggle';
import { LabelType, PrintLabelPipe } from '~/app/pipes/PrintLabel';
import { UiStore } from '~/app/store/ui.store';

interface DropdownItem {
  id: string;
  attrVef?: string;
  disabled?: boolean;
  group?: string;
}
@Component({
  selector: 'gplus-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  imports: [
    CwDropdown,
    CwDropdownItem,
    PrintLabelPipe,
    FormsModule,
    NgTemplateOutlet,
    TranslateModule,
    CustomShadowCssDirective,
    DropdownMenuToggleDirective,
    CwDropdownGroup
  ],
  host: {
    '[style.--dropdown-width]': 'width()'
  }
})
export class DropdownComponent {
  private readonly elm = inject(ElementRef);
  private readonly uiStore = inject(UiStore);

  public control = input<AbstractControl>();
  public data = input<Array<DropdownItem>>();
  public multiple = input<boolean>();
  public placeholder = input<string>();
  public required = input<boolean>();
  public disabled = input<boolean>();
  public readonly = input<boolean>();
  public attrVef = input<string>();
  public validations = input<Record<string, any>>({});
  public labelType = input<LabelType>('default');
  public loading = input<boolean>();
  public status = input<InputStatus>();
  public assistiveText = input<string>();
  public noDataMessage = input<string>();
  public label = input<string>();

  public menuToggle = output<boolean>();

  @ContentChild('itemTemplate') itemTemplate: TemplateRef<any>;
  @ContentChild('footerTemplate') footerTemplate: TemplateRef<any>;

  // Safari fix: dropdown items are not shown in Safari without this fix
  public safariFix = '.dropdown-panel{height: auto;max-height: 440px;}';
  // Assistive text does not grow on height and could potentially cut the text, we need this fix
  public assistiveTextFix = 'cw-input-assistive{height: auto!important;}';

  public error = signal<string>(null);
  public width = computed<string>(() => {
    // we track changes in the screen size to update the width accordingly
    this.uiStore.windowSize();

    // to calculate again the width of the component
    const width = this.elm?.nativeElement?.offsetWidth;
    return width ? width + 'px' : null;
  });

  public inputTextValue = toSignal<Record<string, unknown> | Array<Record<string, unknown>>>(
    toObservable(this.control).pipe(
      filter((control) => !!control),
      switchMap((control: AbstractControl) =>
        control.valueChanges.pipe(
          startWith(this.findValues(control.value)),
          map((value) => this.findValues(value)),
          tap(() => {
            if (this.control().pristine) {
              this.error.set('');
            } else {
              this.error.set(
                Object.keys(this.validations()).reduce((err, key) => {
                  return err || (this.control().hasError(key) ? this.validations()[key] : null);
                }, null)
              );
            }
          })
        )
      )
    ),
    { initialValue: null }
  );

  public groups = computed<Array<{ items: Array<DropdownItem>; key: string }>>(() => {
    return this.groupValues(this.data());
  });

  private groupValues(values: Array<DropdownItem>): Array<{ items: Array<DropdownItem>; key: string }> {
    const groupMap = values?.reduce<{ [key: string]: Array<DropdownItem> }>(
      (map, d) => {
        if (!d.group) {
          // we add a special key to the map to store non-grouped items
          map._nonGrouped.push(d);
          return map;
        }

        map[d.group] = map[d.group] || [];
        map[d.group].push(d);
        return map;
      },
      { _nonGrouped: [] }
    );

    if (!groupMap) {
      return [];
    }

    return (
      Object.keys(groupMap)
        .map((key) => ({ items: groupMap[key], key }))
        // we sort to have the non-grouped items at the beginning
        .toSorted((a, b) => a.key.localeCompare(b.key))
    );
  }

  /**
   *  Find the values in the data array that match the control value
   * @param controlValue
   * @returns
   */
  private findValues(controlValue: { id: string } | Array<{ id: string }>): any {
    if (!controlValue) {
      return this.multiple ? [] : null;
    }

    if (Array.isArray(controlValue)) {
      return this.data()?.filter(
        (d) => (controlValue as Array<{ id: string }>)?.findIndex((v) => v.id === d.id) !== -1
      );
    } else {
      return this.data()?.find((d) => d.id === (controlValue as { id: string }).id);
    }
  }

  /**
   *  Handle the selection of an item
   * @param selection
   * @returns
   */
  public selected(selection: Record<string, unknown>) {
    if (!this.control()) {
      return;
    }

    this.control().setValue(selection);
    this.control().markAsDirty();
  }
}
